import { DateTime as DT } from 'luxon';
import { TaskTypeStatus } from '@econtractor/types';
import { useTranslation } from 'react-i18next';

interface DashDeadlineProps {
    dateStart?: number | null;
    dateEnd?: number | null;
    taskStatus: number;
}

const deadlineTone = (elapsedPercent: number): 'success' | 'warning' | 'danger' => {
    if (elapsedPercent >= 80) return 'danger';
    if (elapsedPercent >= 50) return 'warning';
    return 'success';
};

const toMillis = (value?: number | null): number => {
    const millis = Number(value);
    return Number.isFinite(millis) && millis > 0 ? millis : 0;
};

const DashDeadline = ({ dateStart, dateEnd, taskStatus }: DashDeadlineProps) => {
    const { t } = useTranslation(['Tasks']);
    const endMs = toMillis(dateEnd);
    const endDt = endMs ? DT.fromMillis(endMs, { zone: 'local' }) : null;

    if (!endDt?.isValid) {
        return <span className="object-analytics-dash__deadline-date text-muted">—</span>;
    }

    const now = Date.now();
    const isCancelled =
        taskStatus === TaskTypeStatus.CANCELLED || taskStatus === TaskTypeStatus.REJECT;
    const isDone =
        taskStatus === TaskTypeStatus.COMPLETED || taskStatus === TaskTypeStatus.ACCEPTED;
    const overdue = !isCancelled && !isDone && now > endMs;
    const daysLeft = Math.max(0, Math.ceil((endMs - now) / 86400000));
    const startMs = toMillis(dateStart);
    const span = endMs - startMs;
    const elapsedPercent = span > 0
        ? Math.min(100, Math.max(0, ((now - startMs) / span) * 100))
        : overdue ? 100 : 0;
    const tone = deadlineTone(elapsedPercent);
    const showBar = !isCancelled && !isDone && span > 0;

    let hint = `${t('term.daysLeft')}: ${daysLeft}`;
    let hintClass = `text-${tone}`;
    if (isCancelled) {
        hint = t('status.3');
        hintClass = 'text-muted';
    } else if (isDone) {
        hint = t('term.completed');
        hintClass = 'text-success';
    } else if (overdue) {
        hint = t('term.overdue');
        hintClass = 'text-danger';
    }

    return (
        <div>
            <div className="object-analytics-dash__deadline">
                <span
                    className={`object-analytics-dash__deadline-date fw-bold ${
                        overdue ? 'text-danger' : 'text-body-emphasis'
                    }`}
                >
                    {endDt.toFormat('dd.MM.yyyy')}
                </span>
                <span className={`object-analytics-dash__deadline-hint ${hintClass}`}>{hint}</span>
            </div>
            {showBar ? (
                <div
                    className="object-analytics-dash__track"
                    role="progressbar"
                    aria-valuenow={Math.round(elapsedPercent)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={hint}
                >
                    <div
                        className={`object-analytics-dash__deadline-fill bg-${tone}`}
                        style={{ width: `${elapsedPercent}%` }}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default DashDeadline;
